import {
    AfterViewInit,
    Component,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    NavigationEnd,
    Router,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
} from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoService } from '@jsverse/transloco';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { AuthService } from 'app/core/auth/auth.service';
import { KioskSessionService } from 'app/core/services/kiosk-session.service';
import { Subject, filter, takeUntil } from 'rxjs';
import { KioskAgentComponent } from './kiosk-agent/kiosk-agent.component';
import { KioskScreensaverComponent } from './kiosk-screensaver/kiosk-screensaver.component';

/**
 * Kiosk layout shell for the 42" portrait panel.
 *
 * Minimal by design (modeled on the `empty` layout): no Fuse navigation
 * chrome, drawers or landscape breakpoints. The panel is a single-purpose
 * portrait surface — portrait sizing lives in `kiosk.component.scss`.
 */
@Component({
    selector: 'kiosk-layout',
    templateUrl: './kiosk.component.html',
    styleUrls: ['./kiosk.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        FuseLoadingBarComponent,
        RouterOutlet,
        RouterLink,
        RouterLinkActive,
        MatIconModule,
        KioskAgentComponent,
        KioskScreensaverComponent,
    ],
})
export class KioskLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /** The voice assistant overlay (rendered once for the whole kiosk shell). */
    @ViewChild(KioskAgentComponent) agent?: KioskAgentComponent;

    /**
     * The assistant is only wired up on pages that register a voice manifest.
     * Today that's the Packages station, so the top-bar button only appears
     * there. New agent-enabled kiosk pages can extend this list.
     */
    agentAvailable = false;

    /**
     * Printer kiosks are single-purpose Schedule stations — the page
     * switcher is hidden and Schedule is their only surface. Traders get
     * the staff switcher (Pricing / Packages / Schedule), matching Admin
     * minus the concept-only TikTok page.
     */
    readonly isScheduleStation: boolean = this._readRole() === 'Printer';

    /** TikTok is an Admin concept page — hide it from Trader (and Printer) switchers. */
    readonly showTikTokNav: boolean = this._readRole() === 'Admin';

    /** Cart arrival board is an Admin kiosk page. */
    readonly showCartsNav: boolean = this._readRole() === 'Admin';

    isCartsPage = false;
    lang: 'en' | 'es' = 'en';

    /**
     * When true the full-screen idle "attract" screensaver is shown over the
     * whole kiosk. Toggled on after {@link _idleMs} of no interaction and off
     * again on the first tap (see {@link _wake}). Never runs on staff-only
     * Schedule stations.
     */
    screensaverActive = false;

    /** Identity of the page behind the screensaver, fed to the overlay. */
    ssPageLabel = '';
    ssPageTagline = '';
    ssShowImagery = false;

    /**
     * Per-page identity for the screensaver, keyed by the kiosk path segment.
     * Each kiosk page "names itself" on the idle screen so a passer-by knows
     * exactly which station they walked up to. `imagery` opts a page into the
     * customer apparel slideshow — only the customer-facing Quote page does;
     * staff stations get a clean branded backdrop.
     */
    private readonly _pageIdentities: Record<
        string,
        { label: string; tagline: string; imagery: boolean }
    > = {
        pricing: {
            label: 'Quote',
            tagline: 'Custom apparel, priced in seconds',
            imagery: true,
        },
        packages: {
            label: 'Shipping & Receiving',
            tagline: 'Receive incoming boxes & ship orders out',
            imagery: false,
        },
        receiving: {
            label: 'Receiving and Loading',
            tagline: 'Match the box on camera to an incoming order',
            imagery: false,
        },
        schedule: {
            label: 'Schedule',
            tagline: 'The live production schedule',
            imagery: false,
        },
        carts: {
            label: 'Carts',
            tagline: 'Carts arriving beside the kiosk',
            imagery: false,
        },
        tiktok: {
            label: 'TikTok',
            tagline: 'Make a TikTok with your order',
            imagery: false,
        },
        home: {
            label: 'Kiosk',
            tagline: '',
            imagery: false,
        },
    };

    /**
     * Idle timeout before the screensaver appears. Defaults to 4 minutes; a
     * station can override it with `?idleMs=` on the kiosk URL (0 disables it).
     */
    private _idleMs = 4 * 60_000;

    /** Pending "go idle" timer; cleared/rescheduled on every interaction. */
    private _idleTimer: ReturnType<typeof setTimeout> | null = null;

    /** Interaction events that both keep the panel awake and wake it. */
    private readonly _activityEvents: readonly string[] = [
        'pointerdown',
        'pointermove',
        'touchstart',
        'keydown',
        'wheel',
        'scroll',
    ];

    /** Stable bound handler so add/removeEventListener match. */
    private readonly _onActivity = (): void => this._handleActivity();

    constructor(
        private _auth: AuthService,
        private _router: Router,
        private _zone: NgZone,
        private _session: KioskSessionService,
        private _transloco: TranslocoService,
    ) {}

    ngOnInit(): void {
        this.lang = this._transloco.getActiveLang() === 'es' ? 'es' : 'en';
        this._transloco.langChanges$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((lang) => (this.lang = lang === 'es' ? 'es' : 'en'));
        this._syncAgentAvailability(this._router.url);
        this._syncPageIdentity(this._router.url);
        this._router.events
            .pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll),
            )
            .subscribe((e) => {
                this._syncAgentAvailability(e.urlAfterRedirects);
                this._syncPageIdentity(e.urlAfterRedirects);
            });

        this._startIdleWatch();
    }

    ngAfterViewInit(): void {
        // The assistant no longer auto-starts: the kiosk defaults to "no
        // assistant" and the operator turns it on with the top-bar button.
    }

    // -----------------------------------------------------------------------
    // @ Idle screensaver
    // -----------------------------------------------------------------------

    /** Resolve the page-identity shown on the screensaver for a kiosk URL. */
    private _syncPageIdentity(url: string): void {
        const path = (url || '').split(/[?#]/)[0];
        const segment = path.split('/kiosk/')[1]?.split('/')[0] || '';
        this.isCartsPage = segment === 'carts';
        const id = this._pageIdentities[segment] ?? this._pageIdentities['pricing'];
        this.ssPageLabel = id.label;
        this.ssPageTagline = id.tagline;
        this.ssShowImagery = id.imagery;
    }

    setLang(lang: 'en' | 'es'): void {
        this._transloco.setActiveLang(lang);
    }

    /**
     * Begin watching for inactivity. Interaction listeners run outside Angular
     * (they fire constantly) and only re-enter the zone when the screensaver
     * actually needs to appear or dismiss. Runs on every kiosk page; disabled
     * only when the timeout is set to 0.
     */
    private _startIdleWatch(): void {
        this._idleMs = this._readIdleMs();
        if (this._idleMs <= 0) {
            return;
        }
        this._zone.runOutsideAngular(() => {
            this._activityEvents.forEach((evt) =>
                document.addEventListener(evt, this._onActivity, {
                    passive: true,
                    capture: true,
                }),
            );
        });
        this._resetIdleTimer();
    }

    /** Any interaction: dismiss the screensaver if up, otherwise reschedule. */
    private _handleActivity(): void {
        if (this.screensaverActive) {
            this._zone.run(() => this._wake());
            return;
        }
        this._resetIdleTimer();
    }

    /** (Re)arm the go-idle countdown. Runs outside the zone. */
    private _resetIdleTimer(): void {
        if (this._idleTimer !== null) {
            clearTimeout(this._idleTimer);
        }
        this._idleTimer = setTimeout(() => {
            this._zone.run(() => {
                this.screensaverActive = true;
                // Kill any hot mic while the panel is unattended.
                this.agent?.deactivate();
            });
        }, this._idleMs);
    }

    /**
     * Dismiss the screensaver and resume whatever page was open. The customer
     * quote flow gets a clean reset (the pricing page listens) so the next
     * walk-up never inherits a half-built quote; staff pages (Schedule,
     * Packages) simply resume where they were.
     */
    private _wake(): void {
        if (!this.screensaverActive) {
            return;
        }
        this.screensaverActive = false;
        // Reset customer/operator flow pages to a clean startup state so the
        // next walk-up never inherits the previous person's half-built session.
        // Schedule is deliberately excluded — a worker monitoring production
        // idles naturally and must return to exactly what they left.
        const path = this._router.url.split(/[?#]/)[0];
        if (
            path.includes('/kiosk/pricing') ||
            path.includes('/kiosk/packages') ||
            path.includes('/kiosk/receiving')
        ) {
            this._session.requestReset();
        }
        // Keep the assistant button available on agent pages, but leave the
        // assistant off — it was shut down when the panel went idle and its
        // startup default is off, so waking is a fresh, clean start.
        this.agentAvailable = this._isAgentUrl(this._router.url);
        this._resetIdleTimer();
    }

    /** Template handler for the screensaver's own tap. */
    onScreensaverWake(): void {
        this._wake();
    }

    /** Read the `?idleMs=` override from the URL, if present and valid. */
    private _readIdleMs(): number {
        try {
            const raw = new URLSearchParams(window.location.search).get(
                'idleMs',
            );
            if (raw != null) {
                const n = Number(raw);
                if (Number.isFinite(n) && n >= 0) {
                    return n;
                }
            }
        } catch {
            // Ignore — fall back to the default.
        }
        return this._idleMs;
    }

    /**
     * Which realtime engine the assistant runs on. Gemini 3.1 is the shipping
     * default and auto-starts on page load. (The engine picker is hidden for
     * now — the legacy Gemini and Grok engines will be revisited later.)
     */
    agentEngine: 'gemini' | 'gemini2' | 'grok' = 'gemini2';

    /** Toggle the voice assistant on/off (top-bar button). */
    toggleAgent(): void {
        if (this.agent) {
            this.agent.engine = this.agentEngine;
        }
        this.agent?.toggle();
    }

    /** Human-readable label for the current engine (top-bar pill). */
    get agentEngineLabel(): string {
        switch (this.agentEngine) {
            case 'gemini2':
                return 'Gemini 3.1';
            case 'grok':
                return 'Grok';
            default:
                return 'Gemini';
        }
    }

    /** Cycle the assistant engine (Gemini → Gemini 3.1 → Grok); restart if running. */
    toggleAgentEngine(): void {
        this.agentEngine =
            this.agentEngine === 'gemini'
                ? 'gemini2'
                : this.agentEngine === 'gemini2'
                ? 'grok'
                : 'gemini';
        if (!this.agent) {
            return;
        }
        this.agent.engine = this.agentEngine;
        if (this.agent.isRunning) {
            // Restart on the new engine so the change takes effect immediately.
            this.agent.deactivate();
            setTimeout(() => this.agent?.activate(), 200);
        }
    }

    /**
     * Expose the assistant button only on agent-mapped pages, and stop any
     * running session the moment the operator navigates away so the mic never
     * stays hot. The assistant does NOT auto-start — it defaults to off and the
     * operator turns it on with the top-bar button.
     */
    private _syncAgentAvailability(url: string): void {
        const available = this._isAgentUrl(url);
        this.agentAvailable = available;
        if (!available) {
            this.agent?.deactivate();
        }
    }

    /** True when the given kiosk URL is an agent-enabled page (Packages today). */
    private _isAgentUrl(url: string): boolean {
        const path = (url || '').split(/[?#]/)[0];
        return path.includes('/kiosk/packages') || path.includes('/kiosk/receiving');
    }

    /** End the kiosk session and return to the sign-in screen. */
    signOut(): void {
        this._auth.signOut().subscribe({
            next: () => { window.location.href = '/sign-in'; },
            error: () => { window.location.href = '/sign-in'; },
        });
    }

    private _readRole(): string {
        try {
            const raw = localStorage.getItem('userData');
            if (raw && raw !== 'null' && raw !== 'undefined') {
                return JSON.parse(raw)?.role ?? '';
            }
        } catch {
            // Ignore parse errors — treat as no role.
        }
        return '';
    }

    ngOnDestroy(): void {
        if (this._idleTimer !== null) {
            clearTimeout(this._idleTimer);
            this._idleTimer = null;
        }
        this._activityEvents.forEach((evt) =>
            document.removeEventListener(evt, this._onActivity, {
                capture: true,
            }),
        );
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
}
